import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import StatusBadge from '../../../pages/order-management/components/StatusBadge';
import NewClientModal from '../../../components/ui/NewClientModal';

const QuoteModal = ({ quote, isOpen, onClose, onSave, onConvert, mode = 'view', userRole = 'admin' }) => {
  const [formData, setFormData] = useState({
    customer: '',
    phone: '',
    vehicle: '',
    plates: '',
    status: 'cotizada',
    technician: '',
    items: [],
    discount: 0,
    notes: '',
    expirationDate: ''
  });

  const [newItem, setNewItem] = useState({
    type: 'service',
    name: '',
    quantity: 1,
    price: 0
  });

  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([
    { value: 'juan_perez', label: 'Juan Pérez - 612-345-6789' },
    { value: 'maria_garcia', label: 'María García - 612-987-6543' },
    { value: 'carlos_lopez', label: 'Carlos López - 612-456-7890' },
    { value: 'ana_martinez', label: 'Ana Martínez - 612-234-5678' }
  ]);

  useEffect(() => {
    if (quote && isOpen) {
      setFormData({
        customer: quote?.customer || '',
        phone: quote?.phone || '',
        vehicle: quote?.vehicle || '',
        plates: quote?.plates || '',
        status: quote?.status || 'cotizada',
        technician: quote?.technician || '',
        items: quote?.items || [],
        discount: quote?.discount || 0,
        notes: quote?.notes || '',
        expirationDate: quote?.expirationDate || ''
      });
    }
  }, [quote, isOpen]);

  if (!isOpen) return null;

  const vehicleOptions = [
    { value: 'toyota_corolla', label: 'Toyota Corolla 2020 - ABC-123' },
    { value: 'honda_civic', label: 'Honda Civic 2019 - XYZ-789' },
    { value: 'nissan_versa', label: 'Nissan Versa 2021 - DEF-456' },
    { value: 'mazda_3', label: 'Mazda 3 2022 - GHI-012' }
  ];

  const technicianOptions = [
    { value: 'miguel_sanchez', label: 'Miguel Sánchez' },
    { value: 'roberto_diaz', label: 'Roberto Díaz' },
    { value: 'fernando_ruiz', label: 'Fernando Ruiz' }
  ];

  const serviceOptions = [
    { value: 'cambio_aceite', label: 'Cambio de aceite - €45.00' },
    { value: 'alineacion', label: 'Alineación - €60.00' },
    { value: 'balanceo', label: 'Balanceo - €40.00' },
    { value: 'frenos', label: 'Revisión de frenos - €80.00' }
  ];

  const productOptions = [
    { value: 'llanta_185_65_r15', label: 'Llanta 185/65 R15 - €85.00' },
    { value: 'llanta_195_55_r16', label: 'Llanta 195/55 R16 - €95.00' },
    { value: 'aceite_5w30', label: 'Aceite 5W-30 - €35.00' },
    { value: 'filtro_aire', label: 'Filtro de aire - €15.00' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNewClientSave = (newClient) => {
    setCustomerOptions([...customerOptions, newClient]);
    setFormData({ ...formData, customer: newClient?.value, phone: newClient?.phone });
    setIsNewClientModalOpen(false);
  };

  const handleAddItem = () => {
    if (newItem?.name && newItem?.quantity > 0 && newItem?.price > 0) {
      setFormData({
        ...formData,
        items: [...formData?.items, { ...newItem, id: Date.now() }]
      });
      setNewItem({ type: 'service', name: '', quantity: 1, price: 0 });
    }
  };

  const handleRemoveItem = (itemId) => {
    setFormData({
      ...formData,
      items: formData?.items?.filter(item => item?.id !== itemId)
    });
  };

  const calculateSubtotal = () => {
    return formData?.items?.reduce((sum, item) => sum + (item?.quantity * item?.price), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - (formData?.discount || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleConvert = () => {
    onConvert(quote);
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-card rounded-lg shadow-elevation-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {isCreateMode ? 'Nueva Cotización' : isEditMode ? 'Editar Cotización' : 'Detalle de Cotización'}
            </h2>
            {quote && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">Folio: {quote?.folio}</span>
                <StatusBadge status={formData?.status} />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
            aria-label="Cerrar"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Select
                label="Cliente"
                options={customerOptions}
                value={formData?.customer}
                onChange={(value) => handleInputChange('customer', value)}
                disabled={isViewMode}
                required
              />
              {!isViewMode && (
                <button
                  type="button"
                  onClick={() => setIsNewClientModalOpen(true)}
                  className="mt-2 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-smooth"
                >
                  <Icon name="Plus" size={16} />
                  Nuevo Cliente
                </button>
              )}
            </div>

            <Input
              type="tel"
              label="Teléfono"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              disabled={isViewMode}
              required
            />

            <Select
              label="Vehículo"
              options={vehicleOptions}
              value={formData?.vehicle}
              onChange={(value) => handleInputChange('vehicle', value)}
              disabled={isViewMode}
              required
            />

            <Input
              type="text"
              label="Placas"
              value={formData?.plates}
              onChange={(e) => handleInputChange('plates', e?.target?.value)}
              disabled={isViewMode}
              required
            />

            <Select
              label="Técnico Asignado"
              options={technicianOptions}
              value={formData?.technician}
              onChange={(value) => handleInputChange('technician', value)}
              disabled={isViewMode}
            />

            <Input
              type="date"
              label="Fecha de Vencimiento"
              value={formData?.expirationDate}
              onChange={(e) => handleInputChange('expirationDate', e?.target?.value)}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Artículos y Servicios</h3>
            
            {!isViewMode && (
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <Select
                    label="Tipo"
                    options={[
                      { value: 'service', label: 'Servicio' },
                      { value: 'product', label: 'Producto' }
                    ]}
                    value={newItem?.type}
                    onChange={(value) => setNewItem({ ...newItem, type: value })}
                  />

                  <Select
                    label={newItem?.type === 'service' ? 'Servicio' : 'Producto'}
                    options={newItem?.type === 'service' ? serviceOptions : productOptions}
                    value={newItem?.name}
                    onChange={(value) => setNewItem({ ...newItem, name: value })}
                  />

                  <Input
                    type="number"
                    label="Cantidad"
                    value={newItem?.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e?.target?.value) || 1 })}
                    min="1"
                  />

                  <Input
                    type="number"
                    label="Precio"
                    value={newItem?.price}
                    onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e?.target?.value) || 0 })}
                    min="0"
                    step="0.01"
                  />

                  <div className="flex items-end">
                    <Button
                      variant="default"
                      onClick={handleAddItem}
                      iconName="Plus"
                      className="w-full"
                    >
                      Agregar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {formData?.items?.length > 0 ? (
              <div className="space-y-2">
                {formData?.items?.map((item) => (
                  <div
                    key={item?.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                          {item?.type === 'service' ? 'Servicio' : 'Producto'}
                        </span>
                        <span className="font-medium text-foreground">{item?.name}</span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Cantidad: {item?.quantity} × {formatCurrency(item?.price)} = {formatCurrency(item?.quantity * item?.price)}
                      </div>
                    </div>
                    {!isViewMode && (
                      <button
                        onClick={() => handleRemoveItem(item?.id)}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-smooth"
                        aria-label="Eliminar artículo"
                      >
                        <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                <p>No hay artículos agregados</p>
              </div>
            )}
          </div>

          <div className="border-t border-border pt-6">
            <div className="space-y-3">
              <div className="flex justify-between text-base">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium text-foreground">{formatCurrency(calculateSubtotal())}</span>
              </div>

              {(userRole === 'admin' || !isViewMode) && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Descuento:</span>
                  {isViewMode ? (
                    <span className="font-medium text-foreground">{formatCurrency(formData?.discount)}</span>
                  ) : (
                    <Input
                      type="number"
                      value={formData?.discount}
                      onChange={(e) => handleInputChange('discount', parseFloat(e?.target?.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-32 text-right"
                      disabled={userRole !== 'admin'}
                    />
                  )}
                </div>
              )}

              <div className="flex justify-between text-xl font-bold pt-3 border-t border-border">
                <span className="text-foreground">Total:</span>
                <span className="text-primary">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notas
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              disabled={isViewMode}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Notas adicionales sobre la cotización..."
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {isViewMode ? 'Cerrar' : 'Cancelar'}
          </Button>
          
          {isViewMode && quote && (
            <Button
              variant="success"
              onClick={handleConvert}
              iconName="CheckCircle"
              iconPosition="left"
            >
              Convertir a Orden
            </Button>
          )}
          
          {!isViewMode && (
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Save"
              iconPosition="left"
            >
              {isCreateMode ? 'Crear Cotización' : 'Guardar Cambios'}
            </Button>
          )}
        </div>
      </div>

      <NewClientModal
        isOpen={isNewClientModalOpen}
        onClose={() => setIsNewClientModalOpen(false)}
        onSave={handleNewClientSave}
      />
    </div>
  );
};

export default QuoteModal;